import { useMemo, useRef } from 'react';
import { Form, Formik, FormikProps } from 'formik';
import { Row, Col } from 'antd';

import { normalizePhoneFieldValues } from 'common/validation';

import { Typography, QuestionWrapper } from '../../components';
import { InputField, RadioField } from '../../fields';
import PhoneInputField from '../../fields/PhoneInput';
import { CompanyInfoFormValues } from '../../models/Forms';
import { useTranslation } from 'react-i18next';
import { I18nNameSpaces, Languages } from '../../services/i18n';
import { makeCompanyInfoSchema } from './schema';

interface Props {
  error?: boolean;
  initialValues?: Partial<CompanyInfoFormValues>;
  bindSubmit?: (formik: FormikProps<CompanyInfoFormValues>) => void;
  onSubmit: (values: CompanyInfoFormValues) => void;
}

export default function CompanyInfoForm({ onSubmit, initialValues, error, bindSubmit }: Props) {
  const formRef = useRef(undefined);
  const {
    t,
    i18n: { language },
  } = useTranslation(I18nNameSpaces.Forms);
  const schema = useMemo(() => makeCompanyInfoSchema(t, language), [language, t]);
  const defaultPhonePrefix = language === Languages.Czech ? '420' : '421';

  function handleOnSubmit(values: CompanyInfoFormValues) {
    onSubmit && onSubmit(values);
  }

  function renderForm(formik: FormikProps<CompanyInfoFormValues>) {
    if (bindSubmit) {
      bindSubmit(formik);
    }
    return (
      <div className="company-form">
        <QuestionWrapper.Divider>
          <Form>
            <Row gutter={20}>
              <Col>
                <RadioField
                  name="gender"
                  type="horizontal"
                  options={[
                    {
                      label: t('companyForm.gender.options.mr'),
                      value: 'mr',
                    },
                  ]}
                  label={t('companyForm.gender.label')}
                />
              </Col>
            </Row>
            <Row gutter={20}>
              <Col xs={24} md={8}>
                <InputField
                  name="title"
                  label={t('companyForm.title.label')}
                  placeholder={t('companyForm.title.placeholder')}
                />
              </Col>
              <Col xs={24} md={8}>
                <InputField
                  name="firstName"
                  label={t('companyForm.firstName.label')}
                  placeholder={t('companyForm.firstName.placeholder')}
                />
              </Col>
              <Col xs={24} md={8}>
                <InputField
                  name="lastName"
                  label={t('companyForm.lastName.label')}
                  placeholder={t('companyForm.lastName.placeholder')}
                />
              </Col>
            </Row>
            <Row gutter={20}>
              <Col xs={24} md={8}>
                <InputField
                  name="partnerNumber"
                  label={t('companyForm.partnerNumber.label')}
                  placeholder={t('companyForm.partnerNumber.placeholder')}
                />
              </Col>
              <Col xs={24} md={8}>
                <InputField
                  name="ico"
                  label={t('companyForm.ico.label')}
                  placeholder={t('companyForm.ico.placeholder')}
                />
              </Col>
              <Col xs={24} md={8}>
                <InputField
                  name="address"
                  label={t('companyForm.address.label')}
                  placeholder={t('companyForm.address.placeholder')}
                />
              </Col>
            </Row>
            <Row gutter={20}>
              <Col xs={24} md={8}>
                <InputField
                  name="email"
                  label={t('companyForm.email.label')}
                  placeholder={t('companyForm.email.placeholder')}
                />
              </Col>
              <Col xs={24} md={8}>
                <PhoneInputField
                  name="phone"
                  label={t('companyForm.phone.label')}
                  placeholder={t('companyForm.phone.placeholder')}
                />
              </Col>
            </Row>
            {error ? <Typography.Paragraph type="danger">{t('companyForm.serverError')}</Typography.Paragraph> : null}
          </Form>
        </QuestionWrapper.Divider>
      </div>
    );
  }

  return (
    <Formik
      ref={formRef as any}
      initialValues={{
        // The 'gender' radio button is only a visual hint with hardcoded value
        // It could be removed from the form data model, but customer has requested hiding/disabling it instead
        gender: 'mr',
        firstName: initialValues?.firstName ?? '',
        lastName: initialValues?.lastName ?? '',
        partnerNumber: initialValues?.partnerNumber ?? '',
        ico: initialValues?.ico ?? '',
        address: initialValues?.address ?? '',
        email: initialValues?.email ?? '',
        phone: normalizePhoneFieldValues(
          initialValues?.phone as CompanyInfoFormValues['phone'] | string | undefined,
          defaultPhonePrefix,
        ),
        title: initialValues?.title ?? '',
      }}
      onSubmit={handleOnSubmit}
      validationSchema={schema}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {renderForm}
    </Formik>
  );
}
